import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShoppingCart, Books } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { TPT_PRODUCTS } from "@/data/tptProducts";
import { AMAZON_BOOKS, SHOW_DONT_TELL } from "@/data/storeLinks";

const SHOWCASE = [
  ...AMAZON_BOOKS.filter((b) => b.cover).map((b) => ({ id: `amz-${b.id}`, src: b.cover, alt: `${b.title} paperback cover`, href: "/resources#amazon" })),
  ...TPT_PRODUCTS.filter((p) => p.featured || p.category === "vocabulary").slice(0, 7).map((p) => ({ id: `tpt-${p.id}`, src: p.image, alt: `${p.title} printable cover`, href: "/resources#tpt" })),
];

const Cover = ({ item, size }) => (
  <a href={item.href} className={`block ${size} shrink-0 snap-center`} data-testid={`showcase-cover-${item.id}`} aria-label={item.alt}>
    <img src={item.src} alt={item.alt} className="w-full h-full object-contain bg-white rounded-xl border border-[#d4dff0] shadow-sm" loading="lazy" draggable={false} />
  </a>
);

const useMotionMode = () => {
  const [mode, setMode] = useState("marquee");
  useEffect(() => {
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sm = window.matchMedia("(max-width: 639px)");
    const update = () => setMode(rm.matches || sm.matches ? "swipe" : "marquee");
    update();
    rm.addEventListener("change", update);
    sm.addEventListener("change", update);
    return () => { rm.removeEventListener("change", update); sm.removeEventListener("change", update); };
  }, []);
  return mode;
};

const FADE = { maskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)", WebkitMaskImage: "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)" };

export const GrownUpsShowcase = () => {
  const mode = useMotionMode();
  return (
    <section id="resources" className="py-14 sm:py-20 bg-[#fffdf7] border-y border-[#d4dff0] scroll-mt-28" aria-labelledby="resources-title">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <span className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-[#7358b3]">Resources for grown-ups</span>
        <h2 id="resources-title" className="font-nunito font-extrabold tracking-tight text-3xl sm:text-4xl text-[#17284b] mt-2 leading-tight">
          Resources for Grown-Ups
        </h2>
        <p className="font-nunito text-base sm:text-lg text-[#596882] mt-3 max-w-2xl mx-auto">
          Supporting children with thoughtfully designed printables, workbooks, and teaching tools.
        </p>
      </div>

      <div className="mt-10 h-[200px] sm:h-[280px]" style={FADE} data-testid="showcase-strip" data-mode={mode}>
        {mode === "marquee" ? (
          <Marquee speed={22} pauseOnHover pauseOnClick gradient={false} autoFill className="h-full">
            {SHOWCASE.map((item) => (
              <div key={item.id} className="px-3 h-[200px] sm:h-[280px] flex items-center">
                <Cover item={item} size="w-[150px] h-[190px] sm:w-[210px] sm:h-[262px]" />
              </div>
            ))}
          </Marquee>
        ) : (
          <div className="h-full flex items-center gap-4 overflow-x-auto snap-x snap-mandatory px-6 scrollbar-hide" style={{ scrollbarWidth: "none" }} data-testid="showcase-swipe-row">
            {SHOWCASE.map((item) => (
              <Cover key={item.id} item={item} size="w-[150px] h-[190px] sm:w-[210px] sm:h-[262px]" />
            ))}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a href="/resources#tpt" data-testid="showcase-tpt-btn">
          <Button className="w-full sm:w-auto bg-[#0f285f] hover:bg-[#15367a] text-white font-nunito font-bold rounded-full px-7 py-6 border-0">
            <ShoppingCart size={20} weight="bold" className="mr-2" /> Shop Printable Resources on TpT
          </Button>
        </a>
        <a href="/resources#amazon" data-testid="showcase-amazon-btn">
          <Button variant="outline" className="w-full sm:w-auto bg-white text-[#0f285f] hover:bg-[#f2effb] font-nunito font-bold rounded-full px-7 py-6 border-2 border-[#0f285f]">
            <Books size={20} weight="bold" className="mr-2" /> Explore Paperback Workbooks on Amazon
          </Button>
        </a>
      </div>

      {/* Show, Don't Tell — small preview (max 3 real worksheet images) */}
      <div className="max-w-5xl mx-auto px-4 mt-16" data-testid="sdt-preview">
        <div className="bg-white border border-[#d4dff0] rounded-3xl p-6 sm:p-8 grid lg:grid-cols-5 gap-6 items-center">
          <div className="lg:col-span-2">
            <span className="font-nunito font-bold text-xs uppercase tracking-[0.2em] text-[#ff8518]">Featured collection</span>
            <h3 className="font-nunito font-extrabold tracking-tight text-2xl text-[#17284b] mt-2">Show, Don't Tell Writing</h3>
            <p className="font-nunito text-sm sm:text-base text-[#596882] mt-2 leading-relaxed">
              Real worksheet pages for grades 2–3: directions, example sentences, multiple-choice, tracing, and writing space — Feelings, Places &amp; Settings, and Character Traits.
            </p>
            <a href="/resources#show-dont-tell" data-testid="sdt-explore-btn" className="inline-block mt-4">
              <Button className="bg-[#7358b3] hover:bg-[#5f479a] text-white font-nunito font-bold rounded-full px-6 border-0">
                Explore the Show, Don't Tell Collection <ArrowRight size={16} weight="bold" className="ml-2" />
              </Button>
            </a>
          </div>
          <div className="lg:col-span-3 grid grid-cols-3 gap-3">
            {SHOW_DONT_TELL.slice(0, 3).map((s) => (
              <a key={s.id} href="/resources#show-dont-tell" data-testid={`sdt-preview-${s.id}`}>
                <img src={s.cover} alt={`${s.title} worksheet preview`} className="w-full aspect-[3/4] object-cover object-top rounded-xl border border-[#d4dff0] bg-white" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
